import { useMemo, useState } from 'react';

const DuesQueue = ({ paymentRequests = [], directory = [], onCollect }) => {
  const [search, setSearch] = useState('');
  const directoryById = useMemo(
    () => new Map(directory.map((item) => [item.id, item])),
    [directory],
  );

  const items = useMemo(() => {
    const open = paymentRequests.filter((request) =>
      ['pending', 'processing'].includes(request.status),
    );

    const needle = search.trim().toLowerCase();
    const enriched = open.map((request) => {
      const student = directoryById.get(request.studentId) || null;
      return {
        ...request,
        student,
      };
    });

    const filtered = !needle
      ? enriched
      : enriched.filter((request) => {
          const name = request.student?.displayName?.toLowerCase() || '';
          const academyId = request.student?.academyStudentId?.toLowerCase() || '';
          const course = String(request.courseName || '').toLowerCase();
          return (
            name.includes(needle) ||
            academyId.includes(needle) ||
            course.includes(needle)
          );
        });

    return filtered.sort((a, b) => {
      const aDate = new Date(a.dueDate || a.createdAt || 0).getTime();
      const bDate = new Date(b.dueDate || b.createdAt || 0).getTime();
      return aDate - bDate;
    });
  }, [paymentRequests, directoryById, search]);

  const totalDue = items.reduce(
    (sum, request) => sum + Number(request.amount || 0),
    0,
  );

  return (
    <div className="space-y-3">
      <div className="rounded-2xl border border-stone-200 bg-white px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
              Open dues
            </p>
            <p className="mt-0.5 text-2xl font-semibold text-stone-950">
              LKR {totalDue.toLocaleString()}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
              Requests
            </p>
            <p className="mt-0.5 text-2xl font-semibold text-stone-950">
              {items.length}
            </p>
          </div>
        </div>
        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Filter by name, ID, or course"
          className="mt-3 block min-h-[48px] w-full rounded-2xl border border-stone-300 px-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
        />
      </div>

      {!items.length ? (
        <div className="rounded-2xl border border-dashed border-stone-300 bg-white px-4 py-10 text-center text-sm text-stone-600">
          No open dues. All settled for now.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((request) => (
            <button
              key={request.id}
              type="button"
              onClick={() => onCollect?.(request)}
              className="w-full rounded-2xl border border-stone-200 bg-white px-4 py-3 text-left transition hover:border-stone-300 active:scale-[0.99]"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-base font-semibold text-stone-950">
                    {request.student?.displayName || request.studentName || 'Student'}
                  </p>
                  <p className="truncate text-xs text-stone-600">
                    {request.student?.academyStudentId || '—'} ·{' '}
                    {request.courseName || 'Course'}
                  </p>
                  <p className="mt-0.5 text-xs text-stone-500">
                    {request.paymentType || 'Payment'} · due{' '}
                    {request.dueDate || '—'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold text-stone-950">
                    LKR {Number(request.amount || 0).toLocaleString()}
                  </p>
                  <span className="mt-1 inline-block rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800">
                    {request.status}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DuesQueue;
